import { memo } from 'react'
import { Box, Paper } from '@mui/material'
import OiAnalysisChartPanel from './OiAnalysisChartPanel'
import OiAnalysisTimeSlider from './OiAnalysisTimeSlider'
import OiAnalysisFooter from './OiAnalysisFooter'
import { OiChartRow } from './oiChartData'
import AddToLayoutButton from '../../components/optionChain/header/addToLayoutButton'
import { WidgetTypes } from '../../components/widgetDashboard/widgetDashboard.enums'
import { ChartConfig } from '../../components/chartV2/chart.typings'

const CHART_MOUNT_SX = {
    flex: 1,
    minHeight: 0,
    transition: 'height 0s !important, flex 0s !important, min-height 0s !important',
} as const

const PAPER_SX = {
    borderRadius: 0,
    height: '100%',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'height 0s !important, flex 0s !important, min-height 0s !important',
} as const

export type OiAnalysisChartViewProps = {
    isOIChangeScreen: boolean
    chartData: OiChartRow[]
    showOI: boolean
    replayKey: number
    loading: boolean
    minuteMap: number[]
    value: [number, number]
    onSliderChange: (_: unknown, v: number | number[]) => void
    onSliderCommit: (_: unknown, v: number | number[]) => void
    onQuickSelect: (minutes: number | 'full') => void
    showTimeSlider: boolean
    footerDateLabel: string
    totalCallOi: number
    totalPutOi: number
    callOiChange: number
    putOiChange: number
    compact?: boolean
    showFooter?: boolean
    isWidget?: boolean
    onHoverRow?: (row: OiChartRow | null) => void
    addToLayoutChartConfig?: Partial<ChartConfig>
}

const OiChartMount = memo(({
    chartData,
    showOI,
    replayKey,
    isWidget,
    onHoverRow,
}: Pick<OiAnalysisChartViewProps, 'chartData' | 'showOI' | 'replayKey' | 'isWidget' | 'onHoverRow'>) => (
    <OiAnalysisChartPanel
        chartData={chartData}
        showOI={showOI}
        replayKey={replayKey}
        scaleSide={isWidget ? 'right' : 'left'}
        showLegend={!isWidget}
        showTooltip={!isWidget}
        onHoverRow={onHoverRow}
    />
))

OiChartMount.displayName = 'OiChartMount'

const OiAnalysisChartView = ({
    isOIChangeScreen,
    chartData,
    showOI,
    replayKey,
    loading,
    minuteMap,
    value,
    onSliderChange,
    onSliderCommit,
    onQuickSelect,
    showTimeSlider,
    footerDateLabel,
    totalCallOi,
    totalPutOi,
    callOiChange,
    putOiChange,
    compact = false,
    showFooter = true,
    isWidget = false,
    onHoverRow,
    addToLayoutChartConfig,
}: OiAnalysisChartViewProps) => (
    <Paper
        key={`oi-chart-${isOIChangeScreen ? 'change' : 'interest'}`}
        sx={{
            ...PAPER_SX,
            ...(isWidget ? { flex: 1 } : {}),
        }}
    >
        <Box
            sx={{
                ...CHART_MOUNT_SX,
                position: 'relative',
                '& .add-to-layout-hover': {
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 6,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 0.12s ease',
                },
                '&:hover .add-to-layout-hover': {
                    opacity: 1,
                    pointerEvents: 'auto',
                },
            }}
        >
            <OiChartMount
                chartData={chartData}
                showOI={showOI}
                replayKey={replayKey}
                isWidget={isWidget}
                onHoverRow={onHoverRow}
            />
            {!isWidget ? (
                <AddToLayoutButton
                    variant="chartHover"
                    widgetType={WidgetTypes.OPEN_INTEREST_CHART}
                    source={isOIChangeScreen ? 'oi-change-chart' : 'open-interest-chart'}
                    chartConfig={addToLayoutChartConfig}
                />
            ) : null}
        </Box>

        {showTimeSlider ? (
            <OiAnalysisTimeSlider
                loading={loading}
                minuteMap={minuteMap}
                value={value}
                onSliderChange={onSliderChange}
                onSliderCommit={onSliderCommit}
                onQuickSelect={onQuickSelect}
                compact={compact}
            />
        ) : null}

        {showFooter ? (
            <OiAnalysisFooter
                isOIChangeScreen={isOIChangeScreen}
                footerDateLabel={footerDateLabel}
                totalCallOi={totalCallOi}
                totalPutOi={totalPutOi}
                callOiChange={callOiChange}
                putOiChange={putOiChange}
                compact={compact}
            />
        ) : null}
    </Paper>
)

export default OiAnalysisChartView
